import { Fragment, memo } from "react";
import { SearchAutocompleteSkeleton } from "@/components/ui/Skeletons";

interface AutocompleteResult {
  id: string;
  result: string;
}

interface SearchDropdownProps {
  results: AutocompleteResult[];
  query: string;
  isLoading: boolean;
  onSelect: (result: AutocompleteResult) => void;
}

/**
 * Parse BBCode [b]...[/b] from the API into React elements.
 * The API returns bold markers around matching text.
 */
function parseBBCode(text: string) {
  const parts = text.split(/\[b\]|\[\/b\]/);
  // Odd-indexed parts were inside [b]...[/b]
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-bold text-black">
        {part}
      </strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );
}

function SearchDropdown({
  results,
  isLoading,
  onSelect,
}: SearchDropdownProps) {
  if (isLoading && results.length === 0) {
    return <SearchAutocompleteSkeleton />;
  }

  if (results.length === 0) return null;

  return (
    <div className="absolute top-full left-0 z-50 mt-0.5 max-h-[400px] w-full overflow-y-auto rounded-b border border-gray-200 bg-white shadow-lg">
      {results.map((result, index) => (
        <button
          key={`${result.id}-${index}`}
          onClick={() => onSelect(result)}
          className="block w-full border-b border-gray-100 px-4 py-2.5 text-left text-sm text-gray-700 last:border-b-0 hover:bg-gray-50"
        >
          {parseBBCode(result.result)}
        </button>
      ))}
    </div>
  );
}

export default memo(SearchDropdown);
